import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Login from './components/Login';
import AdminDashboard from './components/AdminDashboard';
import SignUp from './components/SignUp';
import LookupModal from './components/LookupModal';
import './App.css';
import { API_URL } from './config';

// Match iOS Safari's address-bar / Dynamic-Island tint to the active theme.
// Values match --bg-soft from each theme so the header bar appears continuous with the system UI.
function setMetaThemeColor(theme) {
  const color = theme === 'light' ? '#ebebea' : '#0a0a0a';
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', color);
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUsername, setAdminUsername] = useState('');
  const [adminRole, setAdminRole] = useState('');
  const [adminDepartmentId, setAdminDepartmentId] = useState(null);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showLookup, setShowLookup] = useState(false);

  const [theme, setTheme] = useState('lcars-dark');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'lcars-dark';
    setTheme(savedTheme);
    document.body.setAttribute('data-theme', savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
    setMetaThemeColor(savedTheme);

    const token = localStorage.getItem('adminToken');
    if (token) {
      axios.post(`${API_URL}/auth/verify-session`, { token })
        .then(res => {
          if (res.data.authenticated) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            setAdminUsername(res.data.username);
            setAdminRole(res.data.role);
            setAdminDepartmentId(res.data.department_id);
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem('adminToken');
          }
        })
        .catch(() => localStorage.removeItem('adminToken'));
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'lcars-dark' ? 'light' : 'lcars-dark';
    setTheme(newTheme);
    document.body.setAttribute('data-theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    setMetaThemeColor(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const handleLoginSuccess = (authData) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${authData.token}`;
    setAdminUsername(authData.username);
    setAdminRole(authData.role);
    setAdminDepartmentId(authData.department_id);
    setIsAuthenticated(true);
    setShowAdminLogin(false);
  };

  const handleLogout = () => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      axios.post(`${API_URL}/auth/logout`, { token }).catch(() => {});
      localStorage.removeItem('adminToken');
    }
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setAdminUsername('');
    setAdminRole('');
    setAdminDepartmentId(null);
    setShowAdminLogin(false);
  };

  if (showAdminLogin && !isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} onCancel={() => setShowAdminLogin(false)} />;
  }

  return (
    <div className="app">
      {showLookup && <LookupModal onClose={() => setShowLookup(false)} />}
      <header className="lcars-header">

        <div className="lcars-header-left">
          <div className="lcars-accent-pills">
            <span className="lcars-pill lcars-pill-top" />
            <span className="lcars-pill lcars-pill-bottom" />
          </div>
          <div className="lcars-title-block">
            <span className="lcars-title-main">SHORE LEAVE</span>
            <span className="lcars-title-sub">VOLUNTEER SYSTEM</span>
          </div>
        </div>

        <div className="lcars-controls">
          {!isAuthenticated && (
            <>
              <button className="lcars-shifts-btn" onClick={() => setShowLookup(true)}>
                <img src="/unitedfederationofplanets.svg" alt="UFP" className="lcars-icon" />
                <span className="lcars-shifts-label">MY SHIFTS</span>
              </button>
            </>
          )}

          <button className="lcars-toggle" onClick={toggleTheme}>
            {theme === 'lcars-dark' ? (
              <>
                <img src="/enterprise.svg" alt="Light Mode" className="lcars-icon" />
                <span className="lcars-btn-label">LIGHT</span>
              </>
            ) : (
              <>
                <img src="/borgtacticalcube.svg" alt="Dark Mode" className="lcars-icon" />
                <span className="lcars-btn-label">DARK</span>
              </>
            )}
          </button>

          {!isAuthenticated && (
            <button className="lcars-admin-btn" onClick={() => setShowAdminLogin(true)}>
              <img src="/Emblem.png" alt="Starfleet Badge" className="starfleet-icon" />
              <span className="lcars-btn-label">COMMAND</span>
            </button>
          )}
        </div>

      </header>

      {isAuthenticated ? (
        <AdminDashboard 
          username={adminUsername} 
          role={adminRole} 
          departmentId={adminDepartmentId} 
          onLogout={handleLogout} 
        />
      ) : (
        <div className="app-wrapper">
          <main className="content">
            <SignUp />
          </main>
        </div>
      )}
    </div>
  );
}

export default App;